'use client'

import React, { useState } from 'react'
import { cn } from '@/utilities/ui'
import { outfit } from '@/fonts'
import { getClientSideURL } from '@/utilities/getURL'
import { validateContactFields } from '@/utilities/formValidation'
import {
  formatAdTrackingForMessage,
  getCaptureAttributionFields,
  markLeadSubmitted,
  clearLeadSubmitted,
} from '@/utilities/adTracking'
import { fireLeadConversions } from '@/lib/gtag-conversions'
import { prepareEnhancedConversionData } from '@/lib/enhanced-conversions'
import { SmsConsentField, SMS_CONSENT_VERSION } from '@/components/Forms/SmsConsentField.client'

const FIELD_STYLE: React.CSSProperties = {
  width: '100%',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid #ccc',
  borderRadius: 0,
  outline: 'none',
  boxShadow: 'none',
  padding: '8px 16px',
  marginBottom: '10px',
  fontSize: '17px',
  color: '#1d1d1b',
  fontFamily: 'var(--font-outfit-family), sans-serif',
}

const PLACEHOLDER_COLOR = '#888'

function Field({
  as: Tag = 'input',
  placeholder,
  type,
  value,
  onChange,
  required,
  rows,
  children,
  isSelect,
  hasError,
  name,
  autoComplete,
  ariaLabel,
}: {
  as?: 'input' | 'textarea' | 'select'
  placeholder?: string
  type?: string
  value: string
  onChange: (v: string) => void
  required?: boolean
  rows?: number
  children?: React.ReactNode
  isSelect?: boolean
  hasError?: boolean
  name?: string
  autoComplete?: string
  ariaLabel?: string
}) {
  const [focused, setFocused] = useState(false)

  const style: React.CSSProperties = {
    ...FIELD_STYLE,
    borderBottomColor: hasError ? '#dc2626' : focused ? '#009BAB' : '#ccc',
    color: isSelect && !value ? PLACEHOLDER_COLOR : '#1d1d1b',
    ...(Tag === 'textarea' ? { resize: 'none', maxHeight: '111px' } : {}),
    ...(isSelect ? { cursor: 'pointer', appearance: 'none', paddingRight: '28px' } : {}),
  }

  if (Tag === 'select') {
    return (
      <div className="relative">
        <select
          name={name}
          aria-label={ariaLabel}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={style}
        >
          <option value="" disabled hidden>
            {placeholder}
          </option>
          {children}
        </select>
        <span
          className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2"
          style={{ color: PLACEHOLDER_COLOR }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 320 512"
            fill="currentColor"
            width={11}
            height={11}
            aria-hidden
          >
            <path d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9S301 192 288 192H32c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z" />
          </svg>
        </span>
      </div>
    )
  }

  if (Tag === 'textarea') {
    return (
      <textarea
        name={name}
        aria-label={ariaLabel}
        placeholder={placeholder}
        required={required}
        rows={rows ?? 5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={style}
      />
    )
  }

  return (
    <input
      type={type ?? 'text'}
      name={name}
      autoComplete={autoComplete}
      aria-label={ariaLabel}
      placeholder={placeholder}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={style}
    />
  )
}

const PROJECT_TYPES = ['Bathroom', 'Kitchen', 'Home Remodel', 'Addition', 'ADU', 'Other']

const ACCENT_BAR = '#4BA3B0'

type Props = {
  /** Identifies which form this submission comes from */
  source?: string
  /** Override the project type options */
  serviceOptions?: string[]
  /** Override the submit button label */
  submitLabel?: string
  /** Show the spacer div between message and button */
  showSpacer?: boolean
  /** Show the trust note below the button */
  showTrustNote?: boolean
  /** Max width of the form */
  maxWidth?: string | number
  /** Wrap the form in a card container (logo + heading + sub-text) */
  showContainer?: boolean
  /** Heading text inside the card */
  containerHeading?: string
  /** Sub-text below the heading inside the card */
  containerSubText?: string
  /** Show the logo inside the card */
  showLogo?: boolean
  /** Logo width in px */
  logoWidth?: number
  /** Logo height in px */
  logoHeight?: number
  /** className for the outer card wrapper */
  containerClassName?: string
  /** Style for the outer card wrapper */
  containerStyle?: React.CSSProperties
  /** Style applied to the form element itself */
  style?: React.CSSProperties
}

export function TabsContactForm({
  source = 'other',
  serviceOptions,
  submitLabel = 'Start Crafting',
  showSpacer = true,
  showTrustNote = true,
  maxWidth = '1140px',
  showContainer = false,
  containerHeading = "Let's Craft Your Home Together",
  containerSubText,
  showLogo = true,
  logoWidth = 123,
  logoHeight = 56.84,
  containerClassName,
  containerStyle,
  style,
}: Props) {
  const [values, setValues] = useState({
    fullName: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  })
  const [website, setWebsite] = useState('') // honeypot
  const [smsConsent, setSmsConsent] = useState(false)
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  function set(key: keyof typeof values, val: string) {
    setValues((v) => ({ ...v, [key]: val }))
    if (fieldErrors[key]) setFieldErrors((e) => ({ ...e, [key]: '' }))
  }

  function validate(): Record<string, string> {
    return validateContactFields(
      {
        fullName: values.fullName,
        email: values.email,
        phone: values.phone,
        service: values.service,
        message: values.message,
      },
      { requireService: true, requireMessage: true },
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      return
    }
    setStatus('loading')
    setFieldErrors({})
    try {
      const res = await fetch(`${getClientSideURL()}/api/site-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          website,
          source,
          fullName: values.fullName,
          email: values.email,
          phone: values.phone,
          service: values.service,
          // Structured attribution fields alongside the human-readable block
          // kept in `message` — see adTracking.getCaptureAttributionFields().
          message: values.message + formatAdTrackingForMessage(),
          ...getCaptureAttributionFields(),
          smsConsent,
          smsConsentVersion: smsConsent ? SMS_CONSENT_VERSION : null,
        }),
      })
      if (res.ok || res.status === 201) {
        setStatus('success')
        // Hard navigation: this form's modal lives in the persistent (frontend)
        // layout, so a soft router.push leaves the full-screen modal overlay
        // pinned on top of /thank-you forever ("Redirecting…" never clears).
        // A full document navigation tears the modal down with the page.
        // Mark first, then try to convert here. If gtag wasn't ready the flag
        // survives and /thank-you fires instead; if it was, we clear so the
        // lead is counted exactly once. The hard nav makes firing before the
        // navigation especially important — a document teardown kills the
        // in-flight Ads request, which is what event_callback guards against.
        const enhancedData = await prepareEnhancedConversionData(values)
        markLeadSubmitted(source, enhancedData)
        if (fireLeadConversions(source, () => window.location.assign('/thank-you'), enhancedData)) {
          clearLeadSubmitted()
        }
      } else {
        const json = await res.json().catch(() => ({}))
        if (json?.errors && typeof json.errors === 'object') {
          setFieldErrors(json.errors)
        }
        setStatus('error')
      }
    } catch {
      setFieldErrors({ form: 'Network error. Please try again.' })
      setStatus('error')
    }
  }

  const options = serviceOptions ?? PROJECT_TYPES

  const ERR = (key: string) =>
    fieldErrors[key] ? (
      <p
        style={{
          color: '#dc2626',
          fontSize: '13px',
          marginTop: '-6px',
          marginBottom: '6px',
          fontFamily: 'var(--font-outfit-family), sans-serif',
        }}
      >
        {fieldErrors[key]}
      </p>
    ) : null

  const formEl = (
    <form
      onSubmit={handleSubmit}
      noValidate
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth,
        margin: '0 auto',
        ...style,
      }}
    >
      {/* Honeypot — humans never see or fill this; bots that do are silently dropped server-side. */}
      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      />

      <Field
        placeholder="Name *"
        required
        value={values.fullName}
        onChange={(v) => set('fullName', v)}
        hasError={!!fieldErrors.fullName}
        name="name"
        autoComplete="name"
        ariaLabel="Full name"
      />
      {ERR('fullName')}
      <Field
        placeholder="Email Address *"
        type="email"
        value={values.email}
        onChange={(val) => set('email', val.replace(/\s/g, ''))}
        hasError={!!fieldErrors.email}
        name="email"
        autoComplete="email"
        ariaLabel="Email address"
      />
      {ERR('email')}
      <Field
        placeholder="Phone Number *"
        type="tel"
        value={values.phone}
        onChange={(val) => set('phone', val.replace(/[^\d()+\-\s.]/g, ''))}
        hasError={!!fieldErrors.phone}
        name="phone"
        autoComplete="tel"
        ariaLabel="Phone number"
      />
      {ERR('phone')}
      <Field
        as="select"
        placeholder="Select Your Project Type *"
        isSelect
        value={values.service}
        onChange={(v) => set('service', v)}
        hasError={!!fieldErrors.service}
        name="project_type"
        ariaLabel="Project type"
      >
        {options.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </Field>
      {ERR('service')}
      <Field
        as="textarea"
        placeholder="Message *"
        required
        rows={5}
        value={values.message}
        onChange={(v) => set('message', v)}
        hasError={!!fieldErrors.message}
        name="message"
        ariaLabel="Message"
      />
      {ERR('message')}

      <SmsConsentField
        checked={smsConsent}
        onChange={setSmsConsent}
        termsUrl="/terms"
        privacyUrl="/privacy"
        id="sms-consent-tabs"
      />

      {showSpacer && <div style={{ height: '200px', backgroundColor: '#ffffff' }} aria-hidden />}

      {fieldErrors.form && (
        <p
          style={{
            color: '#dc2626',
            fontSize: '13px',
            textAlign: 'center',
            fontFamily: 'var(--font-outfit-family), sans-serif',
          }}
        >
          {fieldErrors.form}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'loading' || status === 'success'}
        style={{
          width: '100%',
          background: '#3898A4',
          color: '#fff',
          border: 'none',
          marginTop: style?.flex ? 'auto' : undefined,
          maxHeight: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          fontWeight: 700,
          fontFamily: 'var(--font-outfit-family), sans-serif',
          cursor: 'pointer',
          letterSpacing: '0.02em',
          opacity: status === 'loading' || status === 'success' ? 0.6 : 1,
        }}
      >
        {status === 'loading' ? 'Sending…' : status === 'success' ? 'Redirecting…' : submitLabel}
      </button>

      {showTrustNote && (
        <p
          style={{
            textAlign: 'center',
            fontSize: '16px',
            fontStyle: 'italic',
            color: '#1D1D1B',
            fontFamily: 'var(--font-outfit-family), sans-serif',
          }}
        >
          *Craft <strong style={{ fontStyle: 'italic', fontWeight: 700 }}>never</strong> shares or
          sells your information. We are a local &amp; trusted San Diego Contractor.
        </p>
      )}
    </form>
  )

  if (!showContainer) return formEl

  return (
    <div
      className={cn('p-[10px] rounded-[9px]', containerClassName)}
      style={{
        boxShadow: 'rgba(0,0,0,0.5) 0px 0px 10px 0px',
        backgroundColor: '#ffffff',
        ...containerStyle,
      }}
    >
      <div className="flex flex-col items-center text-center">
        {showLogo && (
          <div
            className="relative mx-auto mt-6 mb-3 max-w-full"
            style={{ width: `${logoWidth}px`, height: `${logoHeight}px` }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              alt="Craft Home Remodeling"
              className="h-full w-full object-contain object-center"
              src="/logo/logo.svg"
            />
          </div>
        )}
        <div className="flex w-full max-w-4xl items-center justify-center gap-3 px-2 sm:gap-4">
          <div
            className="h-px min-w-6 flex-1 max-w-[104px] shrink"
            style={{ backgroundColor: ACCENT_BAR }}
            aria-hidden
          />
          <h2
            className={cn(
              outfit.className,
              'text-[24px] leading-[24px] font-semibold tracking-[1px] text-black whitespace-nowrap',
            )}
          >
            {containerHeading}
          </h2>
          <div
            className="h-px min-w-6 flex-1 max-w-[104px] shrink"
            style={{ backgroundColor: ACCENT_BAR }}
            aria-hidden
          />
        </div>
        {containerSubText && (
          <p
            className={cn(
              outfit.className,
              'mt-4 text-[16px] leading-relaxed text-[#1d1d1b] border-b border-[#009BAB] pb-3 font-thin w-full',
            )}
          >
            {containerSubText}
          </p>
        )}
      </div>
      {formEl}
    </div>
  )
}
